import * as React from 'react';
import { runIfFn } from '..';
import { useCallbackRef } from './use-callback-ref';
/**
 * React hook to manage browser event listeners
 *
 * @param event the event name
 * @param handler the event handler function to execute
 * @param doc the dom environment to execute against (defaults to `document`)
 * @param options the event listener options
 *
 * @internal
 */
export function useEventListener(event, handler, env, options) {
  var listener = useCallbackRef(handler);
  React.useEffect(() => {
    var _runIfFn;
    var node = (_runIfFn = runIfFn(env)) !== null && _runIfFn !== void 0 ? _runIfFn : document;
    node.addEventListener(event, listener, options);
    return () => {
      node.removeEventListener(event, listener, options);
    };
  }, [event, env, options, listener]);
  return () => {
    var _runIfFn2;
    var node = (_runIfFn2 = runIfFn(env)) !== null && _runIfFn2 !== void 0 ? _runIfFn2 : document;
    node.removeEventListener(event, listener, options);
  };
}
export function useEventListeners() {
  var listeners = React.useRef(new Map());
  var currentListeners = listeners.current;
  var add = React.useCallback((el, type, listener, options) => {
    listeners.current.set(listener, {
      type,
      el,
      options
    });
    el.addEventListener(type, listener, options);
  }, []);
  var remove = React.useCallback((el, type, listener, options) => {
    el.removeEventListener(type, listener, options);
    listeners.current.delete(listener);
  }, []);
  React.useEffect(() => () => {
    currentListeners.forEach((value, key) => {
      remove(value.el, value.type, key, value.options);
    });
  }, [remove, currentListeners]);
  return {
    add,
    remove
  };
}